import axios from "axios";
import { InsertOneResult, InsertResult } from "./SharedAPI";
import { Client, Patient, TrackableMeasure, Visit, DocumentRef, SignerData } from "../models/core";
import { Insurance } from "../models/insurance";
import { InsuranceProvider, List } from './ZiphyAPI';

class _PublicAPI {
    readonly baseUrl: string;
    requestId:number;
    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
        this.requestId = 0;
    }

    getClient = async (clientId:string) => { return await this.get<Client>(`clients/${clientId}`); }

    createPatient = async (clientId:string, patient:Omit<Patient, '_id'>, visit:Omit<Visit, '_id'>, insurance:Insurance[],
        measures:TrackableMeasure[], signer:SignerData) => {
        return await this.post<InsertOneResult, unknown>(`clients/${clientId}/patients/create`, {
            patient:patient, visit:visit, insurance:insurance, measures:measures, signer:signer
        });
    }

    createDocument = async (client:Client, patientId:string, check:string,  document:DocumentRef, signer:SignerData) => {
        return await this.post<InsertResult<{url:string, timeout:string}>, unknown>(
            `clients/${client._id.toString()}/patients/createDocument`,
            { patientId:patientId, practiceId:client.practiceId.toString(), document:document, check, signer });
    }

    finalizeDocument = async (clientId:string, patientId:string, documentId:string, signer:SignerData) => {
        return await this.post<InsertOneResult, unknown>(`clients/${clientId}/patients/finalizeDocument`,
            { patientId:patientId, documentId:documentId, signer:signer });
    }

    abortDocument = async (clientId:string, patientId:string, documentId:string, signer:SignerData) => {
        return await this.post<InsertOneResult, unknown>(`clients/${clientId}/patients/abortDocument`,
            { patientId:patientId, documentId:documentId, signer:signer });
    }

    getInsuranceProviders = async () => {
        try {
            const req = await this.get<List<{id:string,type:string,items:InsuranceProvider[]}>>('globals?type=insurance_providers')
            return (req?.items?.length || 0) != 0 ? req?.items[0]?.items || [] : [];
        } catch (error) {
            console.error(error);
        }
        return []
    } 

    getDocumentTypes = async () => {
        try {
            const req = await this.get<List<{id:string,type:string,items:Record<string,string>}>>('globals?type=document_types')
            return (req?.items?.length || 0) != 0 ? req?.items[0]?.items || {} : {};
        } catch (error) {
            console.error(error);
        }
        return {}
    }

    async get<T>(path: string): Promise<T> {
        this.requestId += 1;
        try {
            const response = (await axios.get<T>(
                this.baseUrl + path
            ))
            return response?.data;
        } catch(error) {
            console.error('Error thrown: ', error);
            if(axios.isAxiosError(error)) {
                const errorString = 'error: ' + error.response?.status + ' details: ' + error.response?.data;
                console.error(errorString);
            }
            throw error;
        }
    }

    async post<T, U = T>(path: string, data: U): Promise<T> {
        this.requestId += 1;
        return (await axios.post<T>(
            this.baseUrl + path,
            JSON.stringify({id:this.requestId, data:data}),
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        )).data;
    }
}

export const PublicAPI = new _PublicAPI(process.env.REACT_APP_API_PUBLIC_ENDPOINT || 'http://localhost:5006/');